<template lang="html">
  <div class="harvester-controller">
    <!-- Header Row with Enhanced Styling -->
    <div class="harvester-header" @click="isExpanded = !isExpanded">
      <!-- Left Section: Expand Icon + Title + Stats -->
      <div class="harvester-header__left">
        <div class="harvester-header__expand-btn" :class="{ 'is-expanded': isExpanded }">
          <v-icon size="18" color="grey">mdi-chevron-right</v-icon>
        </div>

        <div class="harvester-header__info">
          <span class="harvester-header__title">{{ harvester.title }}</span>
          <div class="harvester-header__meta">
            <span class="harvester-header__badge harvester-header__badge--count">
              <v-icon x-small class="mr-1">mdi-tag-multiple</v-icon>
              {{ rowData.length }} tags
            </span>
            <span v-if="matchedCount > 0" class="harvester-header__badge harvester-header__badge--matched">
              <v-icon x-small class="mr-1">mdi-check-circle</v-icon>
              {{ matchedCount }} matched
            </span>
          </div>
        </div>
      </div>

      <!-- Right Section: Action Buttons -->
      <div class="harvester-header__actions" @click.stop>
        <!-- Primary Actions Group -->
        <div class="harvester-action-group harvester-action-group--primary">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <button
                v-if="canRemap"
                v-bind="attrs"
                v-on="on"
                class="harvester-action-btn harvester-action-btn--accent"
                @click="remapTagsObjects(harvester)">
                <v-icon size="16">mdi-sync</v-icon>
              </button>
            </template>
            <span>Remap Moniker Tags</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <button
                v-bind="attrs"
                v-on="on"
                class="harvester-action-btn"
                @click="isolateTags(harvester)">
                <v-icon size="16">mdi-crosshairs-gps</v-icon>
              </button>
            </template>
            <span>Isolate Tags in Viewer</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <button
                v-has="permissions.InspectionTags.Create"
                v-bind="attrs"
                v-on="on"
                class="harvester-action-btn"
                @click="createInspection(harvester)">
                <v-icon size="16">mdi-clipboard-check-outline</v-icon>
              </button>
            </template>
            <span>Create Inspection</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <button
                v-has="permissions.TagHarvester.Update"
                v-bind="attrs"
                v-on="on"
                class="harvester-action-btn"
                @click="updateHarvester(harvester)">
                <v-icon size="16">mdi-magnify</v-icon>
              </button>
            </template>
            <span>Update Search</span>
          </v-tooltip>
        </div>

        <!-- Divider -->
        <div class="harvester-action-divider"></div>

        <!-- File Operations Group -->
        <div class="harvester-action-group">
          <input
            type="file"
            style="display: none"
            ref="uploadExcelTags"
            id="uploadExcelTagsInput"
            @change="uploadTagsExcel"
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" />

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <button
                v-bind="attrs"
                v-on="on"
                class="harvester-action-btn"
                :class="{ 'is-loading': isDownloading }"
                :disabled="isDownloading"
                @click="downloadTagsExcel()">
                <v-icon v-if="!isDownloading" size="16">mdi-download</v-icon>
                <v-progress-circular v-else indeterminate size="14" width="2" color="primary"></v-progress-circular>
              </button>
            </template>
            <span>Download Excel</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <button
                v-bind="attrs"
                v-on="on"
                class="harvester-action-btn"
                :class="{ 'is-loading': isUploading }"
                :disabled="isUploading"
                @click="showExcelBrowse()">
                <v-icon v-if="!isUploading" size="16">mdi-upload</v-icon>
                <v-progress-circular v-else indeterminate size="14" width="2" color="primary"></v-progress-circular>
              </button>
            </template>
            <span>Upload Excel</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <button
                v-bind="attrs"
                v-on="on"
                class="harvester-action-btn"
                :class="{ 'is-loading': isPopulating }"
                :disabled="isPopulating"
                @click="showExcelBrowse(true)">
                <v-icon v-if="!isPopulating" size="16">mdi-printer</v-icon>
                <v-progress-circular v-else indeterminate size="14" width="2" color="primary"></v-progress-circular>
              </button>
            </template>
            <span>Populate Properties</span>
          </v-tooltip>
        </div>

        <!-- Divider -->
        <div class="harvester-action-divider"></div>

        <!-- Media & Delete Group -->
        <div class="harvester-action-group">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <button
                v-has="permissions.TagHarvester.Update"
                v-bind="attrs"
                v-on="on"
                class="harvester-action-btn"
                @click="addHarvesterImages(harvester)">
                <v-icon size="16">mdi-camera</v-icon>
              </button>
            </template>
            <span>Capture Thumbnails</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <button
                v-has="permissions.TagHarvester.Delete"
                v-bind="attrs"
                v-on="on"
                class="harvester-action-btn harvester-action-btn--danger"
                :class="{ 'is-loading': isDeleting }"
                :disabled="isDeleting"
                @click="unharvest(harvester)">
                <v-icon v-if="!isDeleting" size="16">mdi-link-off</v-icon>
                <v-progress-circular v-else indeterminate size="14" width="2" color="error"></v-progress-circular>
              </button>
            </template>
            <span>{{ unharvestTooltip }}</span>
          </v-tooltip>
        </div>
      </div>
    </div>

    <!-- Expandable Grid Section -->
    <transition name="harvester-expand">
      <div class="harvester-grid-container" v-if="isExpanded">
        <div class="harvester-grid-toolbar">
          <span class="harvester-grid-toolbar__label">
            <v-icon small class="mr-1">mdi-table</v-icon>
            Tag Details
          </span>
          <span class="harvester-grid-toolbar__selection" v-if="selectedCount > 0">
            {{ selectedCount }} selected
          </span>
        </div>
        <ag-grid-vue
          class="harvester-grid"
          :class="theme"
          @gridReady="onGridReady($event)"
          @selectionChanged="onSelectionChanged"
          rowSelection="multiple"
          pagination="true"
          paginationAutoPageSize="true"
          :rowMultiSelectWithClick="true"
          :columnDefs="columnDefs"
          :defaultColDef="defaultColDef"
          :rowData="rowData"
          :overlayLoadingTemplate="overlayLoadingTemplate"
          :getRowId="getRowId">
        </ag-grid-vue>
      </div>
    </transition>

    <v-dialog :value="remapDialog.active" width="500px" persistent>
      <v-card>
        <div class="d-flex justify-space-between align-center pr-2">
          <v-card-title class="font-weight-bold">
            <v-icon left>mdi-sync</v-icon>
            Remap Objects
          </v-card-title>
        </div>
        <v-divider></v-divider>
        <v-card-text class="py-4">
          <v-sheet
            v-if="remapDialog.loading && remapDialog.remapped <= remapDialog.count"
            height="150px"
            class="d-flex align-center flex-column justify-center">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <span class="mt-3" v-if="!!remapDialog.count"
              >Remapping {{ remapDialog.remapped + 1 }} / {{ remapDialog.count }}</span
            >
          </v-sheet>

          <v-sheet
            v-if="remapDialog.remappingMessage"
            class="d-flex align-center flex-column justify-center">
            <span class="font-weight-bold">{{ remapDialog.remappingMessage }}</span>
          </v-sheet>

          <v-sheet v-if="remapDialog.loading && remapDialog.remapped <= remapDialog.count" class="d-flex justify-center mt-4 w-100">
            <v-sheet width="250px" class="d-flex align-center justify-space-between">
              <div>
                <v-icon large color="success" class="mr-2">mdi-check</v-icon>
                <span>{{ remapDialog.success }}</span>
              </div>
              <div>
                <v-icon large color="error" class="mr-2">mdi-close</v-icon>
                <span>{{ remapDialog.failed }}</span>
              </div>
            </v-sheet>
          </v-sheet>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" @click="cancelMapping">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
  import { AgGridVue } from 'ag-grid-vue';
  import HttpService from '../../../utils/httpService';
  import permissions from '@Share/config/permissions';

  // import loadingIcon from '../../../images/hourglass.gif';
  import cubeLoader from '@public/img/envizion-loader.svg';

  import { $handleError } from '@/utils';
  import { getBBBox } from '@/utils/forgeUtils';
  import { assertPersistableUnitScale, assertElevationSane } from '@/utils/coordinateAuthority';
  import { log, warn, error } from '@/utils/debugLogger';
  import { HARVEST_KEPT_NOTE, facilityLabel, notifyUnharvestResult, tagCountLabel } from '@/utils/harvesterUnharvest';

  export default {
    name: 'HarvesterInfoController',
    components: {
      AgGridVue,
    },
    props: {
      harvester: {
        type: Object,
        required: true,
      },
      search: {
        type: String,
        required: false,
        default: '',
      },
    },
    data() {
      return {
        permissions,
        overlayLoadingTemplate: `<img style="position:absolute;top:50%;left:50%;transform:translate(-50%, -50%);width:80px;height:80px;" src="${cubeLoader}" aria-label="loading">`,
        showProgress: false,
        isExpanded: false,
        isUploading: false,
        isDownloading: false,
        isPopulating: false,
        uploadType: 'importExcel',
        isDeleting: false,
        isLoading: false,
        gridColumnApi: null,
        gridApi: null,
        rowData: [],
        selectedCount: 0,
        columnDefs: [],
        defaultColDef: {
          flex: 1,
          editable: false,
          filter: false,
          sortable: true,
          floatingFilter: false,
          resizable: true,
          cellDataType: false,
          cellRenderer: (data) => {
            return data.value ? data.value : '-';
          },
        },
        abortController: null,
        remapDialog: {
          active: false,
          loading: false,
          remappingMessage: '',
          count: 0,
          remapped: 0,
        },
      };
    },
    beforeMount() {
      this.columnDefs = [
        {
          headerName: 'Tag No.',
          field: 'name',
          colId: 'name',
          sortable: true,
          filter: false,
          resizable: true,
          headerCheckboxSelection: true,
          headerCheckboxSelectionFilteredOnly: true,
          checkboxSelection: true,
        },
        {
          headerName: 'Model Tag',
          field: 'modelTag',
          colId: 'modelTag',
          sortable: true,
          filter: false,
          resizable: true,
        },
        {
          headerName: 'Match',
          field: 'modelTag',
          colId: 'match',
          sortable: true,
          resizable: false,
          headerClass: 'text-center',
          cellRenderer: (params) => {
            if (params.value) {
              return `<i class="fas fa-check" style="color:green;width: 100%;text-align: center;"></i>`;
            } else {
              return `<i class="fas fa-times" style="color:red;width: 100%;text-align: center;"></i>`;
            }
          },
        },
      ];
    },
    mounted() {
      this.isLoading = true;
      HttpService.get({
        url: `api/harvester/getHarvesterTags`,
        params: {
          harvesterId: this.harvester.id,
        },
      })
        .then((response) => {
          this.rowData = response.data ?? [];
          this.performSearch();
        })
        .catch((error) => {
          error(error.message);
        })
        .finally(() => {
          this.isLoading = false;
          this.gridApi?.hideOverlay();
        });
    },
    methods: {
      getRowId(params) {
        return params.data.id;
      },
      onSelectionChanged() {
        this.selectedCount = this.gridApi?.getSelectedNodes()?.length ?? 0;
      },
      onGridReady(params) {
        this.gridApi = params.api;
        this.gridColumnApi = params.columnApi;

        if (this.isLoading) {
          this.gridApi.showLoadingOverlay();
        } else {
          this.performSearch();
        }
      },
      getSelectionRows() {
        let result = this.gridApi?.getSelectedNodes()?.map((node) => node.data);

        if (!result?.length) {
          return this.rowData;
        }

        return result;
      },
      getSelectionIds() {
        let result = this.gridApi?.getSelectedNodes()?.map((node) => node.data.id);

        if (!result?.length) {
          return this.rowData.map((node) => node.id);
        }

        return result;
      },
      updateHarvester(harvester) {
        this.$store.commit('SET_TAG_HARVESTER_INFO', {
          id: harvester.id,
          title: harvester.title,
          masterType: harvester.masterType,
          ...JSON.parse(harvester.metaData),
        });

        this.$store.state.showAddAutoTagsPopup = true;
      },
      createInspection() {
        this.$store.state.tagsToCreateInspection = this.getSelectionRows();
        this.$store.state.showCreateInspectionPopup = true;
      },
      // AVA-787: never deletes a master. Ticked rows are unharvested in this facility; no rows or all
      // rows unharvest the whole harvester and remove it.
      unharvest(harvester) {
        const selected = this.gridApi?.getSelectedNodes()?.map((node) => node.data) ?? [];
        const whole = selected.length === 0 || selected.length === this.rowData.length;
        const facility = facilityLabel(this.$store, harvester.forgeAssetId);
        const tags = tagCountLabel(whole ? this.rowData.length : selected.length);
        const names = selected.slice(0, 5).map((row) => row.name).join(', ')
          + (selected.length > 5 ? ', …' : '');

        this.$dialog.error({
          title: whole ? `Unharvest "${harvester.title}" from ${facility}?` : `Unharvest ${tags} from ${facility}?`,
          text: (whole
            ? `This deletes the "${harvester.title}" harvester in ${facility} and unharvests its ${tags} here: `
              + `their model links and saved positions in ${facility} are removed. `
            : `This removes the model links and saved position in ${facility} of: ${names}. `
              + `The other tags in "${harvester.title}" aren't touched. `)
            + HARVEST_KEPT_NOTE,
          actions: {
            false: 'Cancel',
            true: {
              text: 'Unharvest',
              color: 'error',
              handle: () => this.runUnharvest(harvester, whole ? null : selected.map((row) => row.id), facility),
            },
          },
        });
      },
      async runUnharvest(harvester, selectedIds, facility) {
        this.isDeleting = true;
        try {
          const response = await HttpService.post(selectedIds
            ? { url: `api/harvester/unharvestHarvesterTags/${harvester.id}`, data: selectedIds }
            : { url: `api/harvester/unharvestHarvester/${harvester.id}` });
          notifyUnharvestResult(this.$dialog.notify, harvester.title, facility, response.data);
          if (selectedIds && !response.data?.skipped) {
            this.rowData = this.rowData.filter((node) => !selectedIds.includes(node.id));
          }
          await this.$store.dispatch('UPDATE_HARVESTED_NODES_IDS');
          await this.$store.dispatch('FETCH_ADMIN_HARVESTERS');
        } catch (err) {
          $handleError({ message: `Couldn't unharvest "${harvester.title}" from ${facility}.` });
          error(err.message);
        } finally {
          this.isDeleting = false;
        }
      },
      isolateTags() {
        if (this.showProgress) {
          this.$dialog.notify.warning('Please wait for the current process to finish.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }

        const callback = () => {
          this.showProgress = false;
          let selectedNodesIds = this.getSelectionRows()?.flatMap((node) =>
            node.modelNodes.map((n) => n.dbId),
          );
          this.$store.state.forgeService.getMainExtension().isolateDbIds(selectedNodesIds);
        };

        this.showProgress = true;

        if (this.$store.state.viewer) {
          callback();
        } else {
          this.$dialog.notify.warning('Model is loading, please wait!', {
            position: 'top-right',
            timeout: 3000,
          });
          this.$store.commit('SET_ADMIN_MODEL_GEOMETRY_LOADED_CALLBACK', callback);
          this.$emit('open_3d_viewer');
        }
      },
      showExcelBrowse(populate = false) {
        if (this.isDownloading || this.isUploading || this.isPopulating) return;

        this.uploadType = populate ? 'populateExcel' : 'importExcel';

        this.$refs.uploadExcelTags.click();

        this.isExpanded = !this.isExpanded;
      },
      uploadTagsExcel() {
        if (this.isUploading) return;

        let type = this.harvester.masterType.split(' ')[0].toLowerCase();

        this.isUploading = this.uploadType !== 'populateExcel';
        this.isPopulating = this.uploadType === 'populateExcel';

        let file = this.$refs.uploadExcelTags.files[0];
        let formData = new FormData();
        formData.set('file', file);

        HttpService.post({
          url: `api/tags/${type}/${this.uploadType}/${this.assetId}?ignoreInsert=true`,
          data: formData,
          headers: {
            'Content-Type': false,
            processData: false,
          },
        })
          .then(() => {
            this.$dialog.notify.success('Tags uploaded successfully.', {
              position: 'top-right',
              timeout: 3000,
            });

          })
          .catch(({ message }) => {
            error(message);
            $handleError({
              message: `An Error occurred when deleting tags.`,
            });
          })
          .finally(() => {
            this.isUploading = false;
            this.isPopulating = false;
            this.$refs.uploadExcelTags.value = '';
          });
      },
      downloadTagsExcel() {
        if (this.isDownloading) return;

        this.isDownloading = true;

        const link = document.createElement('a');
        link.setAttribute('download', `${this.harvester.title} tags.xlsx`);
        document.body.appendChild(link);

        HttpService.get({
          url: `api/harvester/exportExcel/${this.harvester.id}`,
          responseType: 'blob',
        })
          .then((response) => {
            let blob = response.data;
            const objectUrl = URL.createObjectURL(blob);
            link.setAttribute('href', objectUrl);
            link.click();
            URL.revokeObjectURL(objectUrl);
          })
          .catch(({ message }) => {
            error(message);

            $handleError({
              message: `Failed to download tags file`,
            });
          })
          .finally(() => {
            document.body.removeChild(link);
            this.isDownloading = false;
          });
      },
      addHarvesterImages() {
        if (this.showProgress) {
          this.$dialog.notify.warning('Please wait for the current process to finish.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }

        this.$dialog.confirm({
          text: 'Do you want to reset all thumbnails?',
          title: 'Confirm',
          actions: {
            false: {
              text: 'No',
              color: 'secondary',
              handle: () => {
                this.getThumbnailsHandler(false);
              },
            },
            true: {
              text: 'Yes, Reset All',
              color: 'primary',
              handle: () => {
                this.getThumbnailsHandler(true);
              },
            },
          },
        });
      },
      getThumbnailsHandler(reset = false) {
        this.$store
          .dispatch('GET_HARVESTER_TAGS_NEEDS_IMAGES_IDS', {
            harvestersIds: [this.harvester.id],
            reset,
          })
          .then((tags) => {
            if (tags.length == 0) {
              this.$dialog.notify.warning('No tags found!', {
                position: 'top-right',
                timeout: 3000,
              });
              return;
            }

            let callback = () => {
              log(`Start capturing thumbnail to harvester ${this.harvester.title}`);
              this.showProgress = false;
              this.$store.commit('SET_FILLING_HARVESTER_TAGS_THUMBNAILS', true);
              this.$store.commit('SET_HARVESTER_TAGS_TO_CAPTURE', tags);
            };

            this.showProgress = true;

            if (this.$store.state.viewer) {
              callback();
            } else {
              this.$dialog.notify.warning('Model is loading, please wait!', {
                position: 'top-right',
                timeout: 3000,
              });
              this.$store.commit('SET_ADMIN_MODEL_GEOMETRY_LOADED_CALLBACK', callback);
              this.$emit('open_3d_viewer');
            }
          })
          .catch((error) => {
            error(error.message);
          });
      },
      performSearch() {
        this.gridApi?.setQuickFilter(this.search);
      },
      closeRemapDialog() {
        this.remapDialog.active = false;
      },
      remapTagsObjects(harvester) {
        this.remapDialog.active = true;
        this.remapDialog.loading = true;

        HttpService.get({
          url: `api/harvester/objectsToRemap/${harvester.id}`,
        })
          .then(({ data }) => {
            this.remapDialog.count = data.length;
            this.remapDialog.remapped = 0;
            this.remapDialog.success = 0;
            this.remapDialog.failed = 0;
            this.remapDialog.remappingMessage = `Remapping ${this.remapDialog.count} objects...`;

            this.remap(data)
              .then(this.updateObjects)
              .then(() => this.updateTagsCenter(harvester.id))
              .then(() => {
                this.$dialog.notify.success('Tags remapped successfully.', {
                  position: 'top-right',
                  timeout: 3000,
                });
              })
              .catch((err) => {
                error('Remap failed:', err);
                $handleError({ message: err.message });
              })
              .finally(() => {
                this.remapDialog.loading = false;
                this.remapDialog.active = false;
              });
          })
          .catch((err) => {
            error('Loading objects to remap failed:', err);
            this.remapDialog.loading = false;
            this.remapDialog.active = false;
            $handleError({ message: err.message });
          });
      },
      async remap(objects) {
        // Coordinate-space gate: remap rewrites Objects3D.Center — never with an
        // unknown unit scale.
        assertPersistableUnitScale(this.viewer?.model);

        this.abortController = new AbortController();
        const signal = this.abortController.signal;

        const concurrencyLimit = 5;
        let promises = [];

        const processObject = async (obj) => {
          if (signal.aborted) {
            throw new DOMException('Operation was canceled', 'AbortError');
          }

          this.remapDialog.remappingMessage = `Remapping ${obj.name} - ${obj.dbId} ...`;

          try {
            const dbIds = await this.searchAsync(obj.moniker, this.abortController);
            this.remapDialog.remapped++;
            if (dbIds.length > 0) {
              this.remapDialog.success++;
              obj.dbId = dbIds[0];
              obj.center = this.forgeService.view3dToWorld(
                getBBBox([obj.dbId], this.viewer.model).getCenter(),
              );
            } else {
              this.remapDialog.failed++;
            }
          } catch (error) {
            if (error.name === 'AbortError') {
              throw error;
            }
          }
        };

        const remapNext = async (index) => {
          if (index >= objects.length || signal.aborted) {
            return;
          }

          try {
            await processObject(objects[index]);
          } finally {
            if (index + concurrencyLimit < objects.length) {
              await remapNext(index + concurrencyLimit);
            }
          }
        };

        for (let i = 0; i < Math.min(concurrencyLimit, objects.length); i++) {
          promises.push(remapNext(i));
        }

        await Promise.all(promises);
        return objects;
      },
      searchAsync(moniker, abortController) {
        return new Promise((resolve, reject) => {
          const signal = abortController.signal;

          if (signal.aborted) {
            reject(new DOMException('Search operation was canceled', 'AbortError'));
            return;
          }

          const abortHandler = () => {
            reject(new DOMException('Search operation was aborted', 'AbortError'));
          };

          signal.addEventListener('abort', abortHandler);

          this.viewer.search(moniker, (dbIds) => {
            if (signal.aborted) {
              signal.removeEventListener('abort', abortHandler);
              return;
            }

            if (dbIds) {
              signal.removeEventListener('abort', abortHandler);
              resolve(dbIds);
            } else {
              reject('No dbIds found');
            }
          });
        });
      },
      updateObjects(objects) {
        return new Promise((resolve, reject) => {
          let batchSize = 100;

          const updateNext = async (index) => {
            if (index >= objects.length) {
              resolve();
              return;
            }

            let batch = objects.slice(index, index + batchSize);

            HttpService.post({
              url: `api/harvester/updateObjects`,
              data: batch,
            })
              .then(() => {
                updateNext(index + batchSize);
              })
              .catch((error) => {
                reject(error);
              });
          };

          updateNext(0);
        });
      },
      async updateTagsCenter(harvesterId) {
        return HttpService.get({
          url: `api/harvester/getHarvesterTags`,
          params: { harvesterId },
        })
          .then(async ({ data }) => {
            // Coordinate-space gate: this heal rewrites tag points (and now
            // children centers) — never run it with an unknown unit scale.
            assertPersistableUnitScale(this.viewer?.model);

            const tagUpdates = [];
            const objectUpdates = [];

            data.forEach((tag) => {
              const dbIds = tag.modelNodes.map((node) => node.dbId);
              if (dbIds.length === 0) return;

              tag.point = this.forgeService.view3dToWorld(
                getBBBox(dbIds, this.viewer.model).getCenter(),
              );

              tagUpdates.push({ id: tag.id, point: tag.point });

              // Symmetric heal: the historical Point-only update left
              // Objects3D.Center stale (the Register-2D mm/m corruption).
              // Children get the same recompute, via the existing
              // updateObjects endpoint. node.id requires the updated
              // getHarvesterTags projection — skip gracefully when absent.
              tag.modelNodes.forEach((node) => {
                if (!node.id || node.dbId == null) return;
                objectUpdates.push({
                  id: node.id,
                  dbId: node.dbId,
                  name: node.name,
                  center: this.forgeService.view3dToWorld(
                    getBBBox([node.dbId], this.viewer.model).getCenter(),
                  ),
                });
              });
            });

            // Systematic wrong-scale check on a sample (level bands are metres).
            if (tagUpdates.length) assertElevationSane(tagUpdates[0].point);

            log(`[TagsCenter] healing ${tagUpdates.length} tag points, ${objectUpdates.length} children centers`);

            if (objectUpdates.length) await this.updateObjects(objectUpdates);

            return HttpService.post({
              // The centres come from this facility's model; a group-owned tag stores them per facility (AVA-786).
              url: `api/harvester/updateHarvesterTagsCenter?assetId=${encodeURIComponent(this.assetId)}`,
              data: tagUpdates,
            });
          })
          .catch((err) => {
            error('Error updating tags center:', err);
            throw err;
          });
      },
      cancelMapping() {
        this.remapDialog.active = false;
        this.abortController.abort();
      },
    },
    computed: {
      assetId() {
        return this.$store.state.assetId;
      },
      unharvestTooltip() {
        const facility = facilityLabel(this.$store, this.harvester.forgeAssetId);
        return this.selectedCount && this.selectedCount < this.rowData.length
          ? `Unharvest ${tagCountLabel(this.selectedCount)} from ${facility}`
          : `Unharvest "${this.harvester.title}" from ${facility}`;
      },
      isDarkModeOn: {
        get() {
          return this.$store.getters.GET_DARK_MODE;
        },
        set(value) {
          this.$store.commit('SET_DARK_MODE', value);
        },
      },
      theme() {
        return this.isDarkModeOn ? 'ag-theme-alpine-dark' : 'ag-theme-alpine';
      },
      forgeService() {
        return this.$store.state.forgeService;
      },
      viewer() {
        return this.$store.state.viewer;
      },
      canRemap() {
        return this.$store.state.useMoniker && this.viewer?.model?.isSVF2();
      },
      matchedCount() {
        return this.rowData.filter(row => !!row.modelTag).length;
      },
    },
    watch: {
      search: {
        handler: function () {
          this.performSearch();
        },
        deep: true,
      },
    },
  };
</script>
<style lang="scss" scoped>
/* =============================================================================
   HARVESTER INFO CONTROLLER - Enhanced Premium Styling
   ============================================================================= */

.harvester-controller {
  --harvester-radius: var(--ds-radius-md, 8px);
  --harvester-transition: var(--ds-transition-default, 0.2s ease);
  --harvester-bg: var(--v-bg2-base);
  --harvester-border: var(--ds-border-default, rgba(0, 0, 0, 0.12));
  --harvester-header-height: 56px;

  background: var(--harvester-bg);
  border: 1px solid var(--harvester-border);
  border-radius: var(--harvester-radius);
  overflow: hidden;
  transition: all var(--harvester-transition);

  /* Entrance animation */
  animation: harvester-slide-in 0.35s ease-out backwards;
  animation-delay: var(--harvester-delay, 0ms);

  &:hover {
    border-color: color-mix(in srgb, var(--v-primary-base) 40%, transparent);
    box-shadow: var(--ds-shadow-2, 0 2px 8px rgba(0, 0, 0, 0.1));
  }
}

@keyframes harvester-slide-in {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* -----------------------------------------------------------------------------
   Header Section
   ----------------------------------------------------------------------------- */

.harvester-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: var(--harvester-header-height);
  padding: var(--ds-space-sm, 8px) var(--ds-space-md, 16px);
  cursor: pointer;
  background: linear-gradient(
    135deg,
    transparent 0%,
    var(--ds-surface-hover, rgba(0, 0, 0, 0.02)) 100%
  );
  border-bottom: 1px solid transparent;
  transition: all var(--harvester-transition);
  gap: var(--ds-space-md, 16px);

  &:hover {
    background: var(--ds-surface-hover, rgba(0, 0, 0, 0.04));
  }
}

.harvester-header__left {
  display: flex;
  align-items: center;
  gap: var(--ds-space-sm, 8px);
  flex: 1;
  min-width: 0;
}

.harvester-header__expand-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: var(--ds-radius-sm, 4px);
  background: var(--ds-surface-hover, rgba(0, 0, 0, 0.04));
  transition: all var(--harvester-transition);
  flex-shrink: 0;

  .v-icon {
    transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &.is-expanded .v-icon {
    transform: rotate(90deg);
  }

  &:hover {
    background: var(--v-primary-base);
    .v-icon {
      color: white !important;
    }
  }
}

.harvester-header__info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.harvester-header__title {
  font-size: var(--ds-font-subtitle, 16px);
  font-weight: 600;
  color: var(--v-text-base);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.harvester-header__meta {
  display: flex;
  align-items: center;
  gap: var(--ds-space-sm, 8px);
  flex-wrap: wrap;
}

.harvester-header__badge {
  display: inline-flex;
  align-items: center;
  font-size: var(--ds-font-micro, 10px);
  font-weight: 500;
  padding: 2px 8px;
  border-radius: var(--ds-radius-full, 9999px);
  text-transform: uppercase;
  letter-spacing: 0.3px;

  &--count {
    background: rgba(var(--v-blue-base-rgb, 84, 125, 154), 0.12);
    color: var(--v-blue-base);
  }

  &--matched {
    background: rgba(var(--v-primary-base-rgb, 120, 156, 74), 0.12);
    color: var(--v-primary-base);
  }
}

.theme--dark .harvester-header__badge {
  &--count {
    background: rgba(84, 125, 154, 0.25);
  }
  &--matched {
    background: rgba(3, 98, 76, 0.25);
  }
}

/* -----------------------------------------------------------------------------
   Action Buttons
   ----------------------------------------------------------------------------- */

.harvester-header__actions {
  display: flex;
  align-items: center;
  gap: var(--ds-space-xs, 4px);
  flex-shrink: 0;
}

.harvester-action-group {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px;
  background: var(--ds-surface-hover, rgba(0, 0, 0, 0.03));
  border-radius: var(--ds-radius-sm, 4px);

  &--primary {
    background: rgba(var(--v-primary-base-rgb, 120, 156, 74), 0.08);
  }
}

.theme--dark .harvester-action-group {
  background: rgba(255, 255, 255, 0.05);

  &--primary {
    background: rgba(3, 98, 76, 0.15);
  }
}

.harvester-action-divider {
  width: 1px;
  height: 24px;
  background: var(--ds-border-default, rgba(0, 0, 0, 0.12));
  margin: 0 var(--ds-space-xs, 4px);
}

.harvester-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: var(--ds-radius-sm, 4px);
  background: transparent;
  color: var(--v-grey-base);
  cursor: pointer;
  transition: all var(--harvester-transition);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: currentColor;
    opacity: 0;
    transition: opacity var(--harvester-transition);
  }

  &:hover:not(:disabled) {
    color: var(--v-primary-base);
    background: rgba(var(--v-primary-base-rgb, 120, 156, 74), 0.12);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.is-loading {
    pointer-events: none;
  }

  /* Accent variant (for special actions like remap) */
  &--accent {
    color: var(--v-golden-base, #d2a000);

    &:hover:not(:disabled) {
      color: var(--v-golden-base);
      background: rgba(210, 160, 0, 0.15);
    }
  }

  /* Danger variant (delete) */
  &--danger {
    color: var(--v-error-base, #b71c1c);

    &:hover:not(:disabled) {
      color: var(--v-error-base);
      background: rgba(183, 28, 28, 0.12);
    }
  }
}

/* -----------------------------------------------------------------------------
   Grid Container
   ----------------------------------------------------------------------------- */

.harvester-grid-container {
  border-top: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.08));
  background: var(--v-bg3-base);
}

.harvester-grid-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--ds-space-sm, 8px) var(--ds-space-md, 16px);
  background: linear-gradient(
    180deg,
    var(--ds-surface-hover, rgba(0, 0, 0, 0.02)) 0%,
    transparent 100%
  );
  border-bottom: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.06));
}

.harvester-grid-toolbar__label {
  display: flex;
  align-items: center;
  font-size: var(--ds-font-caption, 12px);
  font-weight: 500;
  color: var(--v-text_secondary-base);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.harvester-grid-toolbar__selection {
  font-size: var(--ds-font-caption, 12px);
  font-weight: 600;
  color: var(--v-primary-base);
  padding: 2px 10px;
  background: rgba(var(--v-primary-base-rgb, 120, 156, 74), 0.1);
  border-radius: var(--ds-radius-full, 9999px);
}

.harvester-grid {
  width: 100%;
  height: 310px;

  /* Custom AG Grid Styling */
  &.ag-theme-alpine,
  &.ag-theme-alpine-dark {
    --ag-row-hover-color: rgba(var(--v-primary-base-rgb, 120, 156, 74), 0.08);
    --ag-selected-row-background-color: rgba(var(--v-primary-base-rgb, 120, 156, 74), 0.12);
    --ag-range-selection-border-color: transparent;
    --ag-header-background-color: var(--v-bg2-base);
    --ag-odd-row-background-color: transparent;
    --ag-row-border-color: var(--ds-border-subtle, rgba(0, 0, 0, 0.06));

    .ag-root-wrapper {
      border: none;
      border-radius: 0;
    }

    .ag-header {
      border-bottom: 1px solid var(--ds-border-default, rgba(0, 0, 0, 0.1));
    }

    .ag-header-cell {
      font-weight: 600;
      font-size: var(--ds-font-caption, 12px);
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }

    .ag-cell {
      font-size: var(--ds-font-body-sm, 13px);
    }

    .ag-paging-panel {
      border-top: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.06));
      background: var(--v-bg2-base);
    }
  }
}

/* -----------------------------------------------------------------------------
   Expand/Collapse Animation
   ----------------------------------------------------------------------------- */

.harvester-expand-enter-active,
.harvester-expand-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.harvester-expand-enter,
.harvester-expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.harvester-expand-enter-to,
.harvester-expand-leave {
  opacity: 1;
  max-height: 400px;
}

/* -----------------------------------------------------------------------------
   Responsive Adjustments
   ----------------------------------------------------------------------------- */

@media (max-width: 960px) {
  .harvester-header {
    flex-wrap: wrap;
    padding: var(--ds-space-sm, 8px);
  }

  .harvester-header__actions {
    width: 100%;
    justify-content: flex-end;
    margin-top: var(--ds-space-xs, 4px);
    padding-top: var(--ds-space-xs, 4px);
    border-top: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.06));
  }

  .harvester-action-btn {
    width: 28px;
    height: 28px;
  }
}

@media (max-width: 600px) {
  .harvester-action-divider {
    display: none;
  }

  .harvester-action-group {
    padding: 2px;
    gap: 1px;
  }

  .harvester-header__badge {
    font-size: 9px;
    padding: 2px 6px;
  }
}
</style>
